import { Modal } from "bootstrap";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import "../StateMachine.css";
import RectangleEditor from "./RectangleEditior";

const StateMachine = () => {
  const MIN_WIDTH_OF_RECTANGLE = 40;
  const MIN_HEIGHT_OF_RECTANGLE = 40;
  const [rectangles, setRectangles] = useState([]);
  const [lines, setLines] = useState([]);
  const [history, setHistory] = useState([{ rectangles: [], lines: [] }]); // History of states
  const [historyIndex, setHistoryIndex] = useState(0); // Current position in history
  const [fromConnection, setFromConnection] = useState(null);
  const [tempLine, setTempLine] = useState(null); // Temporary line for mouse tracking
  const [nightMode, setNightMode] = useState(true);
  const [dragOverRectangle, setDragOverRectangle] = useState();
  const [polylineJoints, setPolylineJoints] = useState(0); // Default to no joints

  const [viewBox, setViewBox] = useState({
    x: 0,
    y: 0,
    width: 1000,
    height: 500,
  }); // Initial SVG viewBox
  const NORMAL_ZOOM = 1.0; // Normal zoom level
  const [zoomLevel, setZoomLevel] = useState(NORMAL_ZOOM); // Default zoom level
  const PAN_STEP = 50; // Step size for panning

  const [isUndoOrRedo, setIsUndoOrRedo] = useState(false);

  const [editRectangle, setEditRectangle] = useState(null); // To track the rectangle being edited
  const [modalValues, setModalValues] = useState({
    caption: "",
    description: "",
  });
  const [editLine, setEditLine] = useState(null); // To track the rectangle being edited
  const [lineModalValues, setLineModalValues] = useState({
    description: "",
  });

  const [selectedRectangle, setSelectedRectangle] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);

  const lineColor = nightMode ? "#80cbc4" : "#1e88e5"; // Default line color
  const lineColorSelected = nightMode ? "#ff5722" : "#ffcc80"; // Selected line color
  const rectangleColor = nightMode ? "#4caf50" : "#a5d6a7"; // Green shades
  const textColor = nightMode ? "#e0e0e0" : "#212121"; // Light Gray for night, Dark Gray for day

  const rectangleColorSelected = nightMode ? "#ff5722" : "#ffcc80"; // Deep Orange for night, Amber for day

  const saveToHistory = (newState) => {
    const newHistory = [...history.slice(0, historyIndex + 1), newState];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const getTransformedCoordinates = (svg, clientX, clientY) => {
    if (!svg) return { x: 0, y: 0 };

    const point = svg.createSVGPoint(); // Create an SVG point
    point.x = clientX;
    point.y = clientY;

    const transformedPoint = point.matrixTransform(
      svg.getScreenCTM().inverse()
    ); // Transform to SVG coordinates
    return { x: transformedPoint.x, y: transformedPoint.y };
  };

  const handleZoom = (zoomIn) => {
    setZoomLevel((prev) => {
      const newZoom = zoomIn ? prev * 1.2 : prev / 1.2;
      const newWidth = 1000 / newZoom;
      const newHeight = 500 / newZoom;
      setViewBox((prevViewBox) => ({
        ...prevViewBox,
        width: newWidth,
        height: newHeight,
      }));
      return newZoom;
    });
  };

  const handlePan = (direction) => {
    setViewBox((prevViewBox) => {
      let { x, y } = prevViewBox;
      if (direction === "left") x -= PAN_STEP;
      if (direction === "right") x += PAN_STEP;
      if (direction === "up") y -= PAN_STEP;
      if (direction === "down") y += PAN_STEP;
      return { ...prevViewBox, x, y };
    });
  };

  const updatePointOfPolyLine = (index, dx, dy, line) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id === line.id) {
          // Clone the points array
          const newPoints = [...l.points];

          // Update only the midpoint
          if (index > 0 && index < newPoints.length - 1) {
            newPoints[index] = {
              x: newPoints[index].x + dx,
              y: newPoints[index].y + dy,
            };
          }

          // Return updated line with new points
          return {
            ...l,
            points: newPoints,
          };
        }
        return l;
      })
    );
  };

  const setToNearestTen = (corner, rectId) => {
    setRectangles((prev) =>
      prev.map((rect) => {
        if (rect.id === rectId) {
          let { x, y, width, height } = rect;

          // Calculate the snapped dimensions
          const snappedWidth =
            width % 10 === 0 ? width : Math.ceil(width / 10) * 10;
          const snappedHeight =
            height % 10 === 0 ? height : Math.ceil(height / 10) * 10;

          // Adjust x or y based on the corner dragged
          const widthAdjustment = snappedWidth - width;
          const heightAdjustment = snappedHeight - height;

          if (corner === "top-left") {
            x -= widthAdjustment;
            y -= heightAdjustment;
          } else if (corner === "top-right") {
            y -= heightAdjustment;
          } else if (corner === "bottom-left") {
            x -= widthAdjustment;
          }

          return {
            ...rect,
            x,
            y,
            width: snappedWidth,
            height: snappedHeight,
            oldPoints: rect.points,
            points: calculatePointsForCornerDragging(
              x,
              y,
              snappedWidth,
              snappedHeight,
              rect.points,
              rect.id
            ),
          };
        }
        return rect;
      })
    );
  };

  const toggleNightMode = () => setNightMode((prev) => !prev);

  const handleRectangleClick = (id) => {
    setSelectedRectangle(id);
    setSelectedLine(null);
  };

  const handleLineClick = (lineId) => {
    setSelectedLine(lineId);
    setSelectedRectangle(null); // Deselect rectangles
  };

  const deselectAll = () => {
    setSelectedLine(null);
    setSelectedRectangle(null);
  };

  const deleteSelectedLine = () => {
    const modal = new Modal(document.getElementById("confirmModal"));
    modal.show();
  };

  const deleteSelectedLineHandler = () => {
    if (selectedLine) {
      setLines((prev) => prev.filter((line) => line.id !== selectedLine));
      setSelectedLine(null);
    }
  };

  const deleteSelectedRectangle = (e) => {
    const modal = new Modal(document.getElementById("confirmModal"));
    modal.show();
  };

  const deleteSelectedRectangleHandler = () => {
    if (selectedRectangle) {
      setRectangles((prev) =>
        prev.filter((rect) => rect.id !== selectedRectangle)
      );

      const fromLines = lines
        .filter((l) => l.fromRectangleId === selectedRectangle)
        .map((l) => l.id);
      const toLines = lines
        .filter((l) => l.toRectangleId === selectedRectangle)
        .map((l) => l.id);

      setLines((prev) =>
        prev.filter(
          (line) => !fromLines.includes(line.id) && !toLines.includes(line.id)
        )
      );

      setSelectedRectangle(null);
    }
  };

  const resetTempLine = () => {
    setFromConnection(null);
    setTempLine(null); // Reset tempLine to null
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        resetTempLine();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (fromConnection) {
        const svg = document.querySelector(
          nightMode ? ".state-machine-svg-night" : ".state-machine-svg"
        );

        const { x, y } = getTransformedCoordinates(svg, e.clientX, e.clientY);

        setTempLine((prev) => ({
          ...prev,
          x1: fromConnection.x,
          y1: fromConnection.y,
          x2: x,
          y2: y,
        }));
      }
    };

    const handleMouseUp = () => {
      setTempLine(null); // Reset tempLine
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [fromConnection, nightMode]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const previousState = history[historyIndex - 1];
      setRectangles(previousState.rectangles);
      setLines(previousState.lines);
      setHistoryIndex(historyIndex - 1);
      setIsUndoOrRedo(true);
      setTimeout(() => {
        setIsUndoOrRedo(false);
      }, 100);
    }
  };

  // Redo action
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setRectangles(nextState.rectangles);
      setLines(nextState.lines);
      setHistoryIndex(historyIndex + 1);
      setIsUndoOrRedo(true);
      setTimeout(() => {
        setIsUndoOrRedo(false);
      }, 100);
    }
  };

  useEffect(() => {
    if (
      !isUndoOrRedo &&
      (history[history.length - 1].rectangles.length !== rectangles.length ||
        history[history.length - 1].lines.length !== lines.length)
    ) {
      saveToHistory({ rectangles, lines });
    }
  }, [lines, rectangles]);

  useEffect(() => {
    rectangles
      .filter((r) => r.oldPoints && r.oldPoints[0].id !== r.points[0].id)
      .forEach((r) => {
        attachLinesToBestCandidatePoints(r.points, r.oldPoints, r.id);
        setRectangles((prev) =>
          prev.map((rect) => {
            if (rect.id === r.id) {
              return { ...rect, oldPoints: null };
            } else {
              return rect;
            }
          })
        );
      });
  }, [rectangles]);

  const attachLinesToBestCandidatePoints = (points, oldPoints, rectId) => {
    const pointIdsUsedInFromLine = lines
      .filter((l) => l.fromRectangleId === rectId)
      .map((l) => l.fromPointId);
    const pointIdsUsedInToLine = lines
      .filter((l) => l.toRectangleId === rectId)
      .map((l) => l.toPointId);

    const oldUsedPoints = oldPoints.filter(
      (point) =>
        pointIdsUsedInFromLine.includes(point.id) ||
        pointIdsUsedInToLine.includes(point.id)
    );

    // Update the lines to use the new candidate point IDs
    oldUsedPoints.forEach((oldPoint) => {
      let bestCandidate = null;
      let minDistance = Infinity;

      points.forEach((newPoint) => {
        const dx = oldPoint.x - newPoint.x;
        const dy = oldPoint.y - newPoint.y;
        if (oldPoint.edge === newPoint.edge) {
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < minDistance) {
            minDistance = distance;
            bestCandidate = newPoint;
          }
        }
      });

      if (bestCandidate) {
        // Update the corresponding line IDs
        setLines((prevLines) =>
          prevLines.map((line) => {
            if (
              line.fromRectangleId === rectId &&
              line.fromPointId === oldPoint.id
            ) {
              console.log("best candidate id: " + bestCandidate.id);
              return {
                ...line,
                fromPointId: bestCandidate.id,
                from: bestCandidate,
              };
            }
            if (
              line.toRectangleId === rectId &&
              line.toPointId === oldPoint.id
            ) {
              console.log("best candidate id: " + bestCandidate.id);
              return {
                ...line,
                toPointId: bestCandidate.id,
                to: bestCandidate,
              };
            }
            return line;
          })
        );
      } else {
        console.log("no candidate");
      }
    });
  };

  // Add a rectangle
  const addRectangle = () => {
    setRectangles((prev) => {
      const id = Date.now();
      const width = 140;
      const height = 70;
      const offset = prev.length * 20; // Offset each new rectangle
      const x = 100 + offset;
      const y = 100 + offset;
      const points = calculatePointsForNewRectangle(x, y, width, height);
      const newRectangles = [
        ...prev,
        {
          id,
          x,
          y,
          width,
          height,
          caption: "Caption", // Add default caption
          description: "Description", // Add default description
          points,
        },
      ];
      return newRectangles;
    });
  };

  const calculatePointsForCornerDragging = (
    x,
    y,
    width,
    height,
    oldPoints,
    rectId
  ) => {
    const points = [];

    // Generate new points for the rectangle
    for (let i = 0; i <= width; i += 10) {
      points.push({ id: uuidv4(), x: x + i, y, edge: "top" }); // Top edge
      points.push({
        id: uuidv4(),
        x: x + i,
        y: y + height,
        edge: "bottom",
      }); // Bottom edge
    }
    for (let i = 10; i < height; i += 10) {
      points.push({
        id: uuidv4(),
        x,
        y: y + i,
        edge: "left",
      }); // Left edge
      points.push({
        id: uuidv4(),
        x: x + width,
        y: y + i,
        edge: "right",
      }); // Right edge
    }

    return points;
  };

  const calculatePointsForNewRectangle = (x, y, width, height) => {
    const points = [];
    for (let i = 0; i <= width; i += 10) {
      points.push({ id: uuidv4(), x: x + i, y, edge: "top" }); // Top edge
      points.push({
        id: uuidv4(),
        x: x + i,
        y: y + height,
        edge: "bottom",
      }); // Bottom edge
    }
    for (let i = 10; i < height; i += 10) {
      points.push({
        id: uuidv4(),
        x,
        y: y + i,
        edge: "left",
      }); // Left edge
      points.push({
        id: uuidv4(),
        x: x + width,
        y: y + i,
        edge: "right",
      }); // Right edge
    }
    return points;
  };

  const handleCornerDrag = (rectangleId, corner, dx, dy) => {
    resetTempLine();
    setRectangles((prev) => {
      return prev.map((rect) => {
        if (rect.id === rectangleId) {
          let { x, y, width, height } = rect;

          // Get all points used by connection lines
          const connectedPoints = lines
            .filter(
              (line) =>
                line.fromRectangleId === rectangleId ||
                line.toRectangleId === rectangleId
            )
            .flatMap((line) => {
              const fromRect = rect.id === line.fromRectangleId;
              const toRect = rect.id === line.toRectangleId;

              return [
                ...(fromRect
                  ? rect.points.filter((p) => p.id === line.fromPointId)
                  : []),
                ...(toRect
                  ? rect.points.filter((p) => p.id === line.toPointId)
                  : []),
              ];
            });

          // Shrink rectangle based on the corner being dragged
          switch (corner) {
            case "top-left":
              // Shrink height and check for overlap
              if (height - dy > MIN_HEIGHT_OF_RECTANGLE) {
                const newTop = y + dy;
                if (!connectedPoints.some((p) => p.y < newTop)) {
                  height -= dy;
                  y += dy;
                }
              }

              // Shrink width and check for overlap
              if (width - dx > MIN_WIDTH_OF_RECTANGLE) {
                const newLeft = x + dx;
                if (!connectedPoints.some((p) => p.x < newLeft)) {
                  width -= dx;
                  x += dx;
                }
              }
              break;

            case "top-right":
              // Increase width
              width += dx;

              // Shrink height and check for overlap
              if (height - dy > MIN_HEIGHT_OF_RECTANGLE) {
                const newTop = y + dy;
                if (!connectedPoints.some((p) => p.y < newTop)) {
                  height -= dy;
                  y += dy;
                }
              }
              break;

            case "bottom-left":
              // Increase height
              height += dy;

              // Shrink width and check for overlap
              if (width - dx > MIN_WIDTH_OF_RECTANGLE) {
                const newLeft = x + dx;
                if (!connectedPoints.some((p) => p.x < newLeft)) {
                  width -= dx;
                  x += dx;
                }
              }
              break;

            case "bottom-right":
              // Increase width and height
              width += dx;
              height += dy;
              break;

            default:
              break;
          }

          // Ensure minimum size
          width = Math.max(MIN_WIDTH_OF_RECTANGLE, width);
          height = Math.max(MIN_HEIGHT_OF_RECTANGLE, height);

          return {
            ...rect,
            x,
            y,
            width,
            height,
            oldPoints: rect.points,
            points: calculatePointsForCornerDragging(
              x,
              y,
              width,
              height,
              rect.points,
              rect.id
            ),
          };
        }
        return rect;
      });
    });
  };

  // Start connecting from a point
  const startConnecting = (pointId, rectangleId) => {
    const rectangle = rectangles.find((r) => r.id === rectangleId);
    const point = rectangle.points.find((p) => p.id === pointId);
    setFromConnection({ pointId, rectangleId, x: point.x, y: point.y });
  };

  const calculateInitialPolylinePoints = (from, to) => {
    if (polylineJoints === 0) {
      return [
        { x: from.x, y: from.y }, // Starting point
        { x: to.x, y: to.y }, // Ending point
      ];
    }

    const dx = (to.x - from.x) / (polylineJoints + 1);
    const dy = (to.y - from.y) / (polylineJoints + 1);

    const points = [
      { x: from.x, y: from.y }, // Starting point
    ];

    for (let i = 1; i <= polylineJoints; i++) {
      points.push({ x: from.x + i * dx, y: from.y + i * dy });
    }

    points.push({ x: to.x, y: to.y }); // Ending point
    return points;
  };

  // Finish connecting to another point
  const finishConnecting = (toPointId, toRectangleId) => {
    setRectangles((prev) =>
      prev.map((rect) => {
        const pointFrom = rect.points.find(
          (p) => p.id === fromConnection.pointId
        );
        const pointTo = rect.points.find((p) => p.id === toPointId);
        if (rect.id === fromConnection.rectangleId) {
          return {
            ...rect,
          };
        } else if (rect.id === toRectangleId) {
          return {
            ...rect,
          };
        }
        return rect;
      })
    );

    if (fromConnection) {
      // Find the `from` and `to` points
      const fromRectangle = rectangles.find(
        (r) => r.id === fromConnection.rectangleId
      );
      const toRectangle = rectangles.find((r) => r.id === toRectangleId);

      // Safely retrieve points
      const from = fromRectangle?.points.find(
        (p) => p.id === fromConnection.pointId
      );
      const to = toRectangle?.points.find((p) => p.id === toPointId);

      const polylinePoints = calculateInitialPolylinePoints(from, to);

      setLines((prev) => [
        ...prev,
        {
          fromPointId: fromConnection.pointId,
          toPointId: toPointId,
          fromRectangleId: fromConnection.rectangleId,
          toRectangleId: toRectangleId,
          id: uuidv4(),
          points: polylinePoints,
          description: "Line",
          from,
          to,
        },
      ]);
      setFromConnection(null);
      setTempLine(null);
    }
  };

  // Handle rectangle dragging
  const handleDrag = (id, dx, dy) => {
    resetTempLine();
    setRectangles((prev) =>
      prev.map((rect) => {
        if (rect.id === id) {
          const updatedPoints = rect.points.map((p) => ({
            ...p,
            x: p.x + dx,
            y: p.y + dy,
          }));
          return {
            ...rect,
            x: rect.x + dx,
            y: rect.y + dy,
            points: updatedPoints,
          };
        }
        return rect;
      })
    );
    //when dragging rectangle point's x and y changes but thoose point must be updated in lines too to prevent issues;
    setLines((prev) =>
      prev.map((line) => {
        if (line.fromRectangleId === id) {
          const from = { ...line.from, x: line.x + dx, y: line.y + dy };
          return { ...line, from };
        }
        if (line.toRectangleId === id) {
          const to = { ...line.to, x: line.x + dx, y: line.y + dy };
          return { ...line, to };
        }
        return line;
      })
    );
  };

  const openEditModal = (rectangle) => {
    setEditRectangle(rectangle);
    setModalValues({
      caption: rectangle.caption,
      description: rectangle.description,
    });
    const modal = new Modal(document.getElementById("editModal"));
    modal.show();
  };

  const openLineEditModal = (line) => {
    setEditLine(line);
    setLineModalValues({
      description: line.description,
    });
    const modal = new Modal(document.getElementById("editLineModal"));
    modal.show();
  };

  const saveChanges = () => {
    setRectangles((prev) =>
      prev.map((rect) =>
        rect.id === editRectangle.id
          ? {
              ...rect,
              caption: modalValues.caption,
              description: modalValues.description,
            }
          : rect
      )
    );
    setEditRectangle(null);
  };

  const saveLineChanges = () => {
    setLines((prev) =>
      prev.map((line) =>
        line.id === editLine.id
          ? {
              ...line,
              description: lineModalValues.description,
            }
          : line
      )
    );
    setEditLine(null);
  };

  return (
    <div>
      <div>
        <button className="btn btn-primary" onClick={addRectangle}>
          Add Rectangle
        </button>
        <div className="btn-group" style={{ marginLeft: "10px" }}>
          <button
            type="button"
            className="btn btn-secondary dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {polylineJoints === 0
              ? "No Polyline"
              : polylineJoints === 1
              ? "1 Joint"
              : polylineJoints === 2
              ? "2 Joints"
              : polylineJoints === 3
              ? "3 Joints"
              : polylineJoints === 4
              ? "4 Joints"
              : "Select Joints"}
          </button>
          <ul className="dropdown-menu">
            <li>
              <button
                className="dropdown-item"
                onClick={() => setPolylineJoints(0)}
              >
                No Polyline
              </button>
            </li>
            <li>
              <button
                className="dropdown-item"
                onClick={() => setPolylineJoints(1)}
              >
                1 Joint
              </button>
            </li>
            <li>
              <button
                className="dropdown-item"
                onClick={() => setPolylineJoints(2)}
              >
                2 Joints
              </button>
            </li>
            <li>
              <button
                className="dropdown-item"
                onClick={() => setPolylineJoints(3)}
              >
                3 Joints
              </button>
            </li>
            <li>
              <button
                className="dropdown-item"
                onClick={() => setPolylineJoints(4)}
              >
                4 Joints
              </button>
            </li>
          </ul>
        </div>

        {(selectedRectangle || selectedLine) && (
          <div style={{ display: "inline-block", marginLeft: "10px" }}>
            <button
              className="btn btn-danger"
              onClick={
                selectedRectangle ? deleteSelectedRectangle : deleteSelectedLine
              }
            >
              🗑️ Delete
              <span className="ms-2">
                {
                  rectangles.find((rect) => rect.id === selectedRectangle)
                    ?.caption
                }
              </span>
            </button>
          </div>
        )}
        <button
          className="btn btn-secondary"
          style={{ marginLeft: "10px" }}
          onClick={toggleNightMode}
        >
          Toggle {nightMode ? "Day" : "Night"} Mode
        </button>

        <button
          className="btn btn-secondary"
          style={{ marginLeft: "10px" }}
          onClick={handleUndo}
          disabled={historyIndex === 0} // Disable if no previous state
        >
          <i className="fas fa-undo"></i> {/* Undo icon */}
          <span style={{ marginLeft: "5px" }}>Undo</span>
        </button>
        <button
          className="btn btn-secondary"
          style={{ marginLeft: "10px" }}
          onClick={handleRedo}
          disabled={historyIndex === history.length - 1} // Disable if no next state
        >
          <i className="fas fa-redo"></i> {/* Redo icon */}
          <span style={{ marginLeft: "5px" }}>Redo</span>
        </button>

        <button
          className="btn btn-primary ms-2"
          onClick={() => handleZoom(true)}
        >
          <i className="fas fa-search-plus me-1"></i>Zoom In
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => handleZoom(false)}
          style={{ marginLeft: "10px" }}
        >
          <i className="fas fa-search-minus me-1"></i>Zoom Out
        </button>
        <span className="zoom-indicator">
          <strong>Zoom:</strong> {zoomLevel.toFixed(2)}{" "}
          {zoomLevel > NORMAL_ZOOM
            ? `(+${(zoomLevel - NORMAL_ZOOM).toFixed(2)})`
            : zoomLevel < NORMAL_ZOOM
            ? `(-${(NORMAL_ZOOM - zoomLevel).toFixed(2)})`
            : "(Normal)"}
        </span>

        <button
          className="btn btn-secondary me-2"
          onClick={() => handlePan("up")}
        >
          ↑ Up
        </button>
        <button
          className="btn btn-secondary me-2"
          onClick={() => handlePan("left")}
        >
          ← Left
        </button>
        <button
          className="btn btn-secondary me-2"
          onClick={() => handlePan("down")}
        >
          ↓ Down
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => handlePan("right")}
        >
          → Right
        </button>
      </div>

      <svg
        width="100%"
        height="600"
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
        className={nightMode ? "state-machine-svg-night" : "state-machine-svg"}
        onClick={(e) => {
          if (e.target.tagName === "svg") deselectAll();
        }}
      >
        <defs>
          <pattern
            id="chessboard"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
          >
            <rect width="5" height="5" fill="black" />
            <rect x="5" y="5" width="5" height="5" fill="black" />
          </pattern>
          <pattern
            id="holes-pattern"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="5" cy="5" r="3" fill="white" />
          </pattern>
        </defs>

        <svg>
          {/* Define Arrow Marker */}
          <defs>
            <marker
              id="tempLineArrow"
              markerWidth="10"
              markerHeight="10"
              refX="5"
              refY="5"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M 0 0 L 10 5 L 0 10 Z" fill={lineColor} />
            </marker>
          </defs>

          {/* Temporary Line with Arrow */}
          {tempLine && (
            <>
              {/* Render circular path with arrow if condition is satisfied */}
              {dragOverRectangle &&
              fromConnection &&
              dragOverRectangle === fromConnection.rectangleId ? (
                <>
                  {/* Arrowhead Definition */}
                  <defs>
                    <marker
                      id={`arrow-tempLine`}
                      markerWidth="10"
                      markerHeight="10"
                      refX="5"
                      refY="5"
                      orient="auto"
                    >
                      <path d="M 0 0 L 10 5 L 0 10 Z" fill={lineColor} />
                    </marker>
                  </defs>

                  {/* Circular Path */}
                  <path
                    //d={`M ${tempLine.x1} ${tempLine.y1} A 50 50 0 1 1 ${tempLine.x2} ${tempLine.y2}`}
                    d={`M ${tempLine.x1} ${tempLine.y1} A ${
                      (Math.sqrt(
                        Math.pow(tempLine.x2 - tempLine.x1, 2) +
                          Math.pow(tempLine.y2 - tempLine.y1, 2)
                      ) /
                        2) *
                      1.2
                    } ${
                      (Math.sqrt(
                        Math.pow(tempLine.x2 - tempLine.x1, 2) +
                          Math.pow(tempLine.y2 - tempLine.y1, 2)
                      ) /
                        2) *
                      1.2
                    } 0 1 1 ${tempLine.x2} ${tempLine.y2}`}
                    fill="none"
                    stroke={lineColor}
                    strokeDasharray="5,5"
                    strokeWidth="1"
                    markerEnd="url(#arrow-tempLine)" // Attach arrow to end of circular path
                  />
                </>
              ) : (
                <>
                  {/* Default dashed line */}
                  <line
                    x1={tempLine.x1}
                    y1={tempLine.y1}
                    x2={tempLine.x2}
                    y2={tempLine.y2}
                    stroke={lineColor}
                    strokeDasharray="5,5"
                    markerEnd="url(#tempLineArrow)" // Attach arrow marker
                  />
                </>
              )}
            </>
          )}
        </svg>

        {lines.map((line) => {
          const isSameRectangle = line.fromRectangleId === line.toRectangleId;

          // Find the `from` and `to` points
          const fromRectangle = rectangles.find(
            (r) => r.id === line.fromRectangleId
          );
          const toRectangle = rectangles.find(
            (r) => r.id === line.toRectangleId
          );

          // Safely retrieve points
          let from = fromRectangle?.points.find(
            (p) => p.id === line.fromPointId
          );
          let to = toRectangle?.points.find((p) => p.id === line.toPointId);

          // Skip rendering this line if `from` or `to` is undefined
          if (!from) {
            from = line.from;
          }

          if (!to) {
            to = line.to;
          }

          if (isSameRectangle) {
            // Calculate the circle's center and radius
            const centerX = (from.x + to.x) / 2;
            const centerY = (from.y + to.y) / 2;

            // Calculate a radius based on the distance between points
            const radius =
              Math.sqrt(
                Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2)
              ) / 2;

            return (
              <g
                key={line.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLineClick(line.id);
                }}
              >
                {/* Arrowhead Definition */}
                <defs>
                  <marker
                    id={`arrow-${line.id}`}
                    markerWidth="10"
                    markerHeight="10"
                    refX="5"
                    refY="5"
                    orient="auto"
                  >
                    <path
                      d="M 0 0 L 10 5 L 0 10 Z"
                      fill={
                        selectedLine && selectedLine === line.id
                          ? lineColorSelected
                          : lineColor
                      }
                    />
                  </marker>
                </defs>

                {/* Circular Path */}
                <path
                  d={`M ${from.x} ${from.y} A ${radius * 1.2} ${
                    radius * 1.2
                  } 0 1 1 ${to.x} ${to.y}`}
                  fill="none"
                  stroke={
                    selectedLine && selectedLine === line.id
                      ? lineColorSelected
                      : lineColor
                  }
                  strokeWidth="1"
                  style={{ cursor: "pointer" }} // Pointer cursor styling
                  markerEnd={`url(#arrow-${line.id})`} // Attach arrow to end of circular path
                />

                {/* Invisible Path for Mouse Detection */}
                <path
                  d={`M ${from.x} ${from.y} A ${radius * 1.2} ${
                    radius * 1.2
                  } 0 1 1 ${to.x} ${to.y}`}
                  fill="none"
                  stroke="transparent"
                  strokeWidth="15" // Larger stroke for easier mouse detection
                  style={{ cursor: "pointer" }} // Pointer cursor styling
                />

                {/* Text Label */}
                {(() => {
                  // Calculate the midpoint angle
                  const angle =
                    Math.atan2(to.y - from.y, to.x - from.x) - Math.PI / 2; // Perpendicular to the chord
                  const midX =
                    (from.x + to.x) / 2 + radius * 2.5 * Math.cos(angle);
                  const midY =
                    (from.y + to.y) / 2 + radius * 2.5 * Math.sin(angle);

                  return (
                    <text
                      x={midX}
                      y={midY}
                      textAnchor="middle"
                      fill={
                        selectedLine && selectedLine === line.id
                          ? lineColorSelected
                          : textColor
                      }
                      fontSize="12"
                      style={{ cursor: "pointer" }} // Pointer cursor styling
                      onDoubleClick={() => openLineEditModal(line)}
                    >
                      {line.description}
                    </text>
                  );
                })()}
              </g>
            );
          } else {
            const polylinePoints = line.points;

            let textX;
            let textY;
            if (polylinePoints.length > 2) {
              // Calculate the center of the polyline for the text
              const centerIndex = Math.floor(polylinePoints.length / 2);
              const centerPoint = polylinePoints[centerIndex];
              textX = centerPoint.x;
              textY = centerPoint.y - 10; // Slightly above the center point
            } else {
              // Calculate the midpoint between the first and last points
              const firstPoint = polylinePoints[0];
              const lastPoint = polylinePoints[polylinePoints.length - 1];
              textX = (firstPoint.x + lastPoint.x) / 2;
              textY = (firstPoint.y + lastPoint.y) / 2 - 10; // Slightly above the midpoint
            }

            if (line.points) {
              polylinePoints[0] = { x: from.x, y: from.y };
              polylinePoints[line.points.length - 1] = { x: to.x, y: to.y };
            }

            return (
              <g
                key={line.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLineClick(line.id);
                }}
              >
                <defs>
                  <marker
                    id={`arrow-${line.id}`}
                    markerWidth="10"
                    markerHeight="10"
                    refX="10"
                    refY="5"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path
                      d="M 0 0 L 10 5 L 0 10 z"
                      fill={
                        selectedLine && selectedLine === line.id
                          ? lineColorSelected
                          : lineColor
                      }
                    />
                  </marker>
                </defs>
                <polyline
                  points={polylinePoints.map((p) => `${p.x},${p.y}`).join(" ")}
                  fill="none"
                  stroke={
                    selectedLine && selectedLine === line.id
                      ? lineColorSelected
                      : lineColor
                  }
                  strokeWidth="1"
                  strokeDasharray={
                    selectedLine && selectedLine === line.id ? "5,5" : ""
                  }
                  markerEnd={`url(#arrow-${line.id})`}
                  style={{ cursor: "pointer" }}
                />
                <polyline
                  points={polylinePoints.map((p) => `${p.x},${p.y}`).join(" ")}
                  fill="none"
                  style={{ cursor: "pointer" }}
                  stroke="transparent"
                  strokeWidth="15"
                />
                {/* Render draggable circles for adjustable points */}
                {polylinePoints
                  .slice(1, polylinePoints.length - 1)
                  .map((point, indx) => {
                    const index = indx + 1;

                    return (
                      <g
                        key={index}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          let lastX = e.clientX;
                          let lastY = e.clientY;

                          const onMouseMove = (moveEvent) => {
                            const dx = moveEvent.clientX - lastX;
                            const dy = moveEvent.clientY - lastY;
                            updatePointOfPolyLine(index, dx, dy, line);
                            lastX = moveEvent.clientX;
                            lastY = moveEvent.clientY;
                          };

                          const onMouseUp = () => {
                            window.removeEventListener(
                              "mousemove",
                              onMouseMove
                            );
                            window.removeEventListener("mouseup", onMouseUp);
                          };

                          window.addEventListener("mousemove", onMouseMove);
                          window.addEventListener("mouseup", onMouseUp);
                        }}
                      >
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r={3} // No circle for fixed points
                          fill="red"
                          style={{
                            cursor: "pointer", // Disable dragging for fixed points
                          }}
                        />
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r={10} // No circle for fixed points
                          fill="transparent"
                          style={{
                            cursor: "pointer", // Disable dragging for fixed points
                          }}
                        />
                      </g>
                    );
                  })}
                <text
                  x={textX}
                  y={textY}
                  textAnchor="middle"
                  fontSize="12"
                  fill={
                    selectedLine && selectedLine === line.id
                      ? lineColorSelected
                      : textColor
                  }
                  style={{ cursor: "pointer" }} // Pointer cursor styling
                  onDoubleClick={() => openLineEditModal(line)}
                >
                  {line.description}
                </text>
              </g>
            );
          }
        })}

        {rectangles.map((rect) => (
          <RectangleEditor
            setToNearestTen={setToNearestTen}
            key={rect.id}
            rect={{
              ...rect,
              fillColor: rectangleColor,
              fillColorSelected: rectangleColorSelected,
              textColor: textColor,
            }}
            openEditModal={openEditModal}
            startConnecting={startConnecting}
            finishConnecting={finishConnecting}
            fromConnection={fromConnection}
            handleDrag={handleDrag}
            handleCornerDrag={handleCornerDrag}
            isSelected={rect.id === selectedRectangle}
            onRectangleClick={() => handleRectangleClick(rect.id)}
            setDragOverRectangle={setDragOverRectangle}
            nightMode={nightMode}
          />
        ))}
      </svg>

      <div
        className="modal fade"
        id="editModal"
        tabIndex="-1"
        aria-labelledby="editModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editModalLabel">
                Edit Rectangle
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Caption</label>
                <input
                  type="text"
                  className="form-control"
                  value={modalValues.caption}
                  onChange={(e) =>
                    setModalValues((prev) => ({
                      ...prev,
                      caption: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={modalValues.description}
                  onChange={(e) =>
                    setModalValues((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveChanges}
                data-bs-dismiss="modal"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="editLineModal"
        tabIndex="-1"
        aria-labelledby="editModalLabel2"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editModalLabel2">
                Edit Line
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={lineModalValues.description}
                  onChange={(e) =>
                    setLineModalValues((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveLineChanges}
                data-bs-dismiss="modal"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="confirmModal"
        tabIndex="-1"
        aria-labelledby="editModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5>
                {rectangles?.find((r) => r.id === selectedRectangle)?.caption}{" "}
                Silinecek!
              </h5>
            </div>
            <div className="modal-body">
              <p>Silmek istediğinize emin misiniz?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" data-bs-dismiss="modal">
                Vazgeç
              </button>
              <button
                className="btn-danger"
                onClick={
                  selectedRectangle
                    ? deleteSelectedRectangleHandler
                    : deleteSelectedLineHandler
                }
                data-bs-dismiss="modal"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateMachine;
